// Copyright (c) Docugami, Inc. All rights reserved.

import { BoundsComponent } from "sdv/components/BoundsComponent";
import { ColorComponent } from "sdv/components/ColorComponent";
import { SimpleTableColumnHeaderComponent } from "sdv/components/SimpleTableColumnHeaderComponent";
import { RenderBox } from "sdv/components/ChunkRenderComponent";
import { TableChunkRenderComponent } from "sdv/components/TableChunkRenderComponent";
import { ComponentId } from "sdv/engine/Component";
import { InputState } from "sdv/input/InputController";
import { combineBoxes } from "store/shared_types";
import { groupBy } from "utils/ArrayUtils";
import { ISystem, Queries } from "../engine/Engine";
import { IResources } from "../Resources";
import { Entity, IEntityManager } from "sdv/engine/Entity";
import { OutlineComponent } from "sdv/components/OutlineComponent";
import { chunkHintColor, chunkSelectedBorderColor } from "components/shell/presentation/AppStyles";
import { getTokenRange } from "sdv/components/ControlComponent";
import { TokenRangeUtils } from "utils/TokenRangeUtils";

/**
 * Updates render data for table chunks
 */
export class TableChunkRenderSystem implements ISystem {
    get updateEnabled() { return true; }

    entities!: IEntityManager;
    resources!: IResources;
    input!: InputState;
    queries: Queries = {
        renderTables: { types: [ComponentId.TableChunkRender, ComponentId.Renderable, ComponentId.Color, ComponentId.Bounds] },
        selectionContextTables: { types: [ComponentId.SelectionContext, ComponentId.Table, ComponentId.TokenRange] },
        selectedTableChunks: { types: [ComponentId.TableChunkRender, ComponentId.Selected, ComponentId.TokenRange] },
        selectedChunks: { types: [ComponentId.Selected, ComponentId.TokenRange] },
        outlineTables: { types: [ComponentId.Outline, ComponentId.TableChunkRender, ComponentId.Bounds] }
    }

    update() {
        this.updateTableRenderBoxes();
        this.updateOutlines();
    }

    private updateTableRenderBoxes() {
        if (!this.resources.get("pagesLoaded").value) {
            return;
        }

        const updateEntity = (chunk: Entity) => {
            const renderComp = chunk.getComponent<TableChunkRenderComponent>(ComponentId.TableChunkRender)!;
            const columnComp = chunk.getComponent<SimpleTableColumnHeaderComponent>(ComponentId.SimpleTableColumnHeader);
            const color = chunk.getComponent<ColorComponent>(ComponentId.Color)!.color;
            if (!!columnComp && !renderComp.columnBoxes) {
                const pageBoxes = groupBy(columnComp.boxes, x => String(x.page));
                renderComp.columnBoxes = Object.values(pageBoxes).map<RenderBox>(x => {
                    return {
                        box: combineBoxes(x),
                        color: color
                    }
                });
            }

            if (!renderComp.boxes) {
                const boxes = chunk.getComponent<BoundsComponent>(ComponentId.Bounds)!.bounds;
                renderComp.boxes = boxes.map(b => {
                    return {
                        box: {
                            ...b
                        },
                        color: color
                    }
                });
            }

            renderComp.boxes?.forEach(b => b.color = color);
            renderComp.columnBoxes?.forEach(b => b.color = color);
        }

        for (const chunk of this.queries.renderTables.query!.entities) {
            updateEntity(chunk);
        }

        for (const chunk of this.queries.outlineTables.query!.entities) {
            if (!chunk.hasComponent(ComponentId.Renderable)) {
                updateEntity(chunk);
            }
        }
    }

    private updateOutlines() {
        const toOutline: Entity[] = [];
        for (const entity of this.queries.selectedTableChunks.query!.entities) {
            toOutline.push(entity);
            entity.ensureComponent<OutlineComponent>({ typeId: ComponentId.Outline, color: chunkSelectedBorderColor }, true);
        }

        for (const entity of this.queries.selectionContextTables.query!.entities) {
            toOutline.push(entity);

            let outlineColor = chunkHintColor;
            const tokenRange = getTokenRange(entity)!;
            for (const selectedEntity of this.queries.selectedChunks.query!.entities) {
                const selectedEntityRange = getTokenRange(selectedEntity)!;
                if (TokenRangeUtils.rangeIsContained(selectedEntityRange, tokenRange)) {
                    outlineColor = chunkSelectedBorderColor;
                }
            }

            entity.ensureComponent<OutlineComponent>({ typeId: ComponentId.Outline, color: outlineColor }, true);
        }

        for (const entity of this.queries.outlineTables.query!.entities) {
            if (!toOutline.includes(entity)) {
                entity.removeComponent(ComponentId.Outline);
            }
        }
    }
}
